import { useEffect, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { IndianRupee, ShoppingBag, Clock, AlertTriangle } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <Card
    className="p-5 border-[#EBE8E2] shadow-sm bg-white"
    data-testid={testid}
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs text-[#7A7873] uppercase tracking-wider font-medium">
          {label}
        </div>
        <div className="text-2xl sm:text-3xl font-heading font-extrabold text-[#2D2C2A] mt-2 font-mono-num">
          {value}
        </div>
      </div>
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${color}1A`, color }}
      >
        <Icon size={20} strokeWidth={1.75} />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api
      .get("/reports/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <div className="p-6 sm:p-8 max-w-7xl" data-testid="dashboard-page">
      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight text-[#2D2C2A]">
          Dashboard
        </h1>
        <p className="text-sm text-[#7A7873] mt-1">
          Today's performance at a glance ·{" "}
          {new Date().toLocaleDateString("en-IN", { dateStyle: "full" })}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={IndianRupee}
          label="Revenue Today"
          value={inr(data?.revenue_today || 0)}
          color="#D95D39"
          testid="stat-revenue"
        />
        <StatCard
          icon={ShoppingBag}
          label="Orders Today"
          value={data?.orders_today ?? "—"}
          color="#6A7F66"
          testid="stat-orders"
        />
        <StatCard
          icon={Clock}
          label="Open Orders"
          value={data?.open_orders ?? "—"}
          color="#2A9D8F"
          testid="stat-open"
        />
        <StatCard
          icon={AlertTriangle}
          label="Low Stock Alerts"
          value={data?.low_stock_count ?? "—"}
          color="#E9C46A"
          testid="stat-stock"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card
          className="lg:col-span-2 p-6 border-[#EBE8E2] shadow-sm bg-white"
          data-testid="weekly-chart-card"
        >
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-heading font-bold text-lg text-[#2D2C2A]">
              Weekly Revenue
            </h3>
            <span className="text-xs text-[#7A7873]">Last 7 days</span>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart
                data={data?.weekly_series || []}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  stroke="#EBE8E2"
                  strokeDasharray="2 4"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#7A7873", fontSize: 11 }}
                  tickFormatter={(d) => d.slice(5)}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#7A7873", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "white",
                    border: "1px solid #EBE8E2",
                    borderRadius: 8,
                  }}
                  formatter={(v) => inr(v)}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#D95D39"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: "#D95D39" }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card
          className="p-6 border-[#EBE8E2] shadow-sm bg-white"
          data-testid="top-items-card"
        >
          <h3 className="font-heading font-bold text-lg text-[#2D2C2A] mb-4">
            Top Items Today
          </h3>
          {!data?.top_items_today?.length ? (
            <div className="text-sm text-[#7A7873] py-8 text-center">
              No sales yet today.
            </div>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart
                  data={data.top_items_today}
                  layout="vertical"
                  margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    stroke="#EBE8E2"
                    strokeDasharray="2 4"
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tick={{ fill: "#7A7873", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={90}
                    tick={{ fill: "#2D2C2A", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "white",
                      border: "1px solid #EBE8E2",
                      borderRadius: 8,
                    }}
                  />
                  <Bar dataKey="qty" fill="#6A7F66" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
